<template>
  <section id="contact" class="section bg-white dark:bg-dark-bg relative overflow-hidden">
    <!-- Background Decoration -->
    <div class="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-accent/5 to-transparent blur-3xl"></div>

    <div class="container-custom relative z-10">
      <!-- Section Header -->
      <div class="text-center mb-16 animate-reveal">
        <h2 class="text-4xl md:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
          Let's Build Something Exceptional
        </h2>
        <p class="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Share your vision, and we'll craft a tailored strategy to achieve your goals
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <!-- Contact Form -->
        <div>
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Name Field -->
            <div>
              <label for="name" class="form-label">
                Name *
              </label>
              <input
                v-model="formData.name"
                type="text"
                id="name"
                class="form-input"
                :class="{ 'border-red-500': errors.name }"
                placeholder="John Doe"
                @blur="validateField('name')"
              />
              <p v-if="errors.name" class="form-error">{{ errors.name }}</p>
            </div>

            <!-- Email Field -->
            <div>
              <label for="email" class="form-label">
                Email *
              </label>
              <input
                v-model="formData.email"
                type="email"
                id="email"
                class="form-input"
                :class="{ 'border-red-500': errors.email }"
                placeholder="john@example.com"
                @blur="validateField('email')"
              />
              <p v-if="errors.email" class="form-error">{{ errors.email }}</p>
            </div>

            <!-- Service Type -->
            <div>
              <label for="service" class="form-label">
                Service Interest *
              </label>
              <select
                v-model="formData.service"
                id="service"
                class="form-input"
                :class="{ 'border-red-500': errors.service }"
                @blur="validateField('service')"
              >
                <option value="">Select a service</option>
                <option value="web">Website Development</option>
                <option value="app">App Development</option>
                <option value="ai">AI Agents</option>
                <option value="consulting">Consulting</option>
              </select>
              <p v-if="errors.service" class="form-error">{{ errors.service }}</p>
            </div>

            <!-- Message Field -->
            <div>
              <label for="message" class="form-label">
                Message *
              </label>
              <textarea
                v-model="formData.message"
                id="message"
                rows="5"
                class="form-input"
                :class="{ 'border-red-500': errors.message }"
                placeholder="Tell us about your project..."
                @blur="validateField('message')"
              ></textarea>
              <p v-if="errors.message" class="form-error">{{ errors.message }}</p>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="isSubmitting"
              :class="{ shake: isShaking }"
              class="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="!isSubmitting">Request Consultation</span>
              <span v-else class="flex items-center justify-center">
                <Loader2 :size="20" class="animate-spin mr-2" />
                Sending...
              </span>
            </button>

            <!-- Success/Error Message -->
            <transition name="fade">
              <div
                v-if="submitMessage"
                :class="[
                  'p-4 rounded-lg flex items-center',
                  submitSuccess
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                ]"
              >
                <CheckCircle v-if="submitSuccess" :size="20" class="mr-2" />
                <AlertCircle v-else :size="20" class="mr-2" />
                <p class="text-sm font-medium">{{ submitMessage }}</p>
              </div>
            </transition>
          </form>
        </div>

        <!-- Contact Information -->
        <div class="space-y-8">
          <!-- Contact Details -->
          <div class="bg-gradient-to-br from-primary to-primary-700 dark:from-accent dark:to-accent-700 rounded-2xl p-8 text-white">
            <h3 class="text-2xl font-display font-bold mb-6">Contact Information</h3>

            <div class="space-y-4">
              <a href="mailto:contact@devolysis.com" class="flex items-center space-x-4 hover:opacity-80 transition-opacity">
                <div class="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail :size="24" />
                </div>
                <div>
                  <p class="font-semibold">Email</p>
                  <p class="text-sm opacity-90">contact@devolysis.com</p>
                </div>
              </a>

              <a href="tel:+13074878301" class="flex items-center space-x-4 hover:opacity-80 transition-opacity">
                <div class="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone :size="24" />
                </div>
                <div>
                  <p class="font-semibold">Phone</p>
                  <p class="text-sm opacity-90">+1 (307) 487-8301</p>
                </div>
              </a>
            </div>
          </div>

          <!-- Response Time -->
          <div class="bg-gray-50 dark:bg-dark-surface rounded-2xl p-6 border border-gray-200 dark:border-dark-border">
            <div class="flex items-start space-x-4">
              <div class="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock :size="24" class="text-accent" />
              </div>
              <div>
                <h4 class="font-bold text-gray-900 dark:text-white mb-2">Quick Response</h4>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  We typically respond within 24 hours on business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { Mail, Phone, Clock, Loader2, CheckCircle, AlertCircle } from 'lucide-vue-next';
import * as yup from 'yup';

const isShaking = ref(false);

const formData = reactive({
  name: '',
  email: '',
  service: '',
  message: ''
});

const errors = reactive({
  name: '',
  email: '',
  service: '',
  message: ''
});

const isSubmitting = ref(false);
const submitMessage = ref('');
const submitSuccess = ref(false);

const schema = yup.object({
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  email: yup.string().required('Email is required').email('Invalid email address'),
  service: yup.string().required('Please select a service'),
  message: yup.string().required('Message is required').min(10, 'Message must be at least 10 characters')
});

const validateField = async (field) => {
  try {
    await schema.validateAt(field, formData);
    errors[field] = '';
  } catch (error) {
    errors[field] = error.message;
  }
};

const validateForm = async () => {
  try {
    await schema.validate(formData, { abortEarly: false });
    Object.keys(errors).forEach(key => errors[key] = '');
    return true;
  } catch (error) {
    error.inner.forEach(err => {
      errors[err.path] = err.message;
    });
    return false;
  }
};

const serviceNames = {
  web: 'Website Development',
  app: 'App Development',
  ai: 'AI Agents',
  consulting: 'Consulting'
};

const handleSubmit = async () => {
  const isValid = await validateForm();

  if (!isValid) {
    isShaking.value = true;
    setTimeout(() => { isShaking.value = false; }, 400);
    return;
  }

  isSubmitting.value = true;
  submitMessage.value = '';

  try {
    const serviceName = serviceNames[formData.service] || formData.service;

    const response = await fetch('https://formsubmit.co/ajax/contact@devolysis.com', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        service: serviceName,
        message: formData.message,
        _subject: `New Contact Form Submission - ${serviceName}`,
        _autoresponse: `Hi ${formData.name},\n\nThank you for contacting Devolysis! We've received your inquiry about ${serviceName}.\n\nOur team will review your message and get back to you within 24 hours.\n\nBest regards,\nThe Devolysis Team`,
        _template: 'table'
      })
    });

    if (response.ok) {
      submitSuccess.value = true;
      submitMessage.value = 'Thank you for your interest! We\'ve received your consultation request and will be in touch within 24 hours.';

      setTimeout(() => {
        formData.name = '';
        formData.email = '';
        formData.service = '';
        formData.message = '';
        submitMessage.value = '';
      }, 5000);
    } else {
      throw new Error('Failed to send message');
    }
  } catch (error) {
    submitSuccess.value = false;
    submitMessage.value = 'Something went wrong. Please try again or email us directly at contact@devolysis.com';
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-8px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-8px); }
  80% { transform: translateX(8px); }
}

.shake {
  animation: shake 0.4s ease-in-out;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
